import React from "react";
import { Check, X, AlertTriangle, Info } from "lucide-react";

export const LegalPage = ({ title, children }) => (
  <div className="max-w-[900px] mx-auto w-full px-6 py-16">
    <h1 className="text-4xl sm:text-5xl font-[700] text-[#0D3B4C] tracking-tight mb-8">{title}</h1>
    <div className="prose max-w-none font-[400] text-[#0D3B4C] leading-relaxed space-y-4">
      {children || (
        <p className="italic text-[#0D3B4C]/70">
          De inhoud van deze pagina wordt binnenkort toegevoegd.
        </p>
      )}
    </div>
  </div>
);

const Article = ({ n, title, children }) => (
  <section className="mt-8" data-testid={`av-artikel-${n}`}>
    <h2 className="text-xl md:text-2xl font-[700] text-[#0D3B4C] tracking-tight mb-3">
      Artikel {n} — {title}
    </h2>
    <div className="space-y-2 font-[400] text-[#0D3B4C] leading-relaxed">{children}</div>
  </section>
);

const OL = ({ children }) => (
  <ol className="list-decimal pl-6 space-y-2 marker:font-[700] marker:text-[#1B6B2E]">{children}</ol>
);
const UL = ({ children }) => (
  <ul className="list-disc pl-6 space-y-1 marker:text-[#B71C1C]">{children}</ul>
);

export const AlgemeneVoorwaarden = () => (
  <LegalPage title="Algemene Voorwaarden">
    <div className="bg-[#FEF9E7] border-2 border-[#E8F5E9] p-5 -mt-2 mb-6" data-testid="av-meta">
      <p className="font-[700] text-[#0D3B4C] text-lg">De Zeeuwse Tori — Algemene Voorwaarden</p>
      <dl className="mt-3 text-sm grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-x-3 gap-y-1">
        <dt className="font-[700]">Versie:</dt><dd>1.3</dd>
        <dt className="font-[700]">Datum:</dt><dd>28 juli 2026</dd>
      </dl>
    </div>

    <p>
      Welkom bij De Zeeuwse Tori. Wij maken Surinaams eten met zorg, vanuit onze keuken aan de Duke
      Ellingtonstraat in Zeeland. Hieronder lees je hoe wij werken, wat je van ons mag verwachten en
      wat wij van jou vragen. Bij het plaatsen van een bestelling ga je akkoord met deze voorwaarden. Simpel.
    </p>

    <Article n={1} title="Wie is wie">
      <OL>
        <li><span className="font-[700]">De Zeeuwse Tori:</span> een eenmanszaak, gevestigd aan de Duke Ellingtonstraat 109.</li>
        <li><span className="font-[700]">Jij (de klant):</span> degene die bij ons een bestelling plaatst — of dat nu voor jezelf is, voor je gezin, of voor een groep.</li>
        <li><span className="font-[700]">Bestelling:</span> de order die je via onze website zeeuwsetori.nl plaatst.</li>
        <li><span className="font-[700]">Overeenkomst:</span> de afspraak tussen jou en ons zodra je bestelling door ons is bevestigd.</li>
        <li><span className="font-[700]">Catering:</span> de Surinaamse gerechten die wij voor jou bereiden en die je bij ons komt ophalen.</li>
      </OL>
    </Article>

    <Article n={2} title="Wanneer gelden deze voorwaarden?">
      <OL>
        <li>Deze voorwaarden gelden voor alles wat je bij ons bestelt. Altijd.</li>
        <li>Willen we samen iets anders afspreken? Dan doen we dat schriftelijk.</li>
        <li>Door een bestelling te plaatsen, ga je akkoord met deze voorwaarden. Daar zijn we duidelijk over.</li>
      </OL>
    </Article>

    <Article n={3} title="Hoe bestellen werkt">
      <OL>
        <li>Bestellen doe je via onze website: zeeuwsetori.nl.</li>
        <li>Je stelt zelf je menu samen uit onze gerechten. Alles wordt per portie verpakt — netjes, warm en klaar om mee te nemen.</li>
        <li>
          Tijdens het bestellen kies je:
          <UL>
            <li>Je ophaaldag</li>
            <li>Je tijdvenster</li>
          </UL>
        </li>
        <li>Je bestelling is definitief zodra je betaald hebt via Mollie.</li>
        <li>Je krijgt van ons een bevestiging per e-mail met daarin: wat je besteld hebt, het totaalbedrag, je ophaaldag, je tijdvenster en het adres waar je moet zijn.</li>
      </OL>
    </Article>

    <Article n={4} title="Prijzen en betalen">
      <OL>
        <li>Alle prijzen op de website zijn in euro's, inclusief 9% BTW.</li>
        <li>Het totaalbedrag hangt af van welke gerechten je kiest en hoeveel porties.</li>
        <li>Betalen doe je via Mollie (iDEAL/WERO). Andere betaalmethoden bieden we niet aan.</li>
        <li>We gaan pas voor je aan de slag zodra je betaling binnen is.</li>
      </OL>
    </Article>

    <Article n={5} title="Ophalen">
      <OL>
        <li>Je haalt je bestelling zelf op, binnen het tijdvenster dat je gekozen hebt.</li>
        <li>Ophaaladres: Duke Ellingtonstraat 109.</li>
        <li>Wij bezorgen niet. Alles is uitsluitend af te halen.</li>
        <li>Kom je niet binnen je tijdvenster? Dan kunnen we niet garanderen dat je gerechten nog op de juiste temperatuur zijn. Je blijft wel het volledige bedrag verschuldigd.</li>
        <li>Zorg zelf voor geschikt vervoer — een vlakke ondergrond en bescherming tegen regen of wind. Je gerechten verdienen een goede rit naar huis.</li>
      </OL>
    </Article>

    <Article n={6} title="Annuleren of wijzigen">
      <p>We snappen dat plannen kunnen veranderen. Maar zodra je bestelling geplaatst en betaald is, is deze definitief. Daar zijn we helder over:</p>
      <OL>
        <li>We geven geen restitutie. Eenmaal betaald, blijft betaald.</li>
        <li>Dit geldt altijd — of je nu minder gasten hebt dan gedacht, iemand ziek is, het weer tegenzit, of om welke andere reden dan ook.</li>
        <li>Haal je je bestelling niet op? Dan blijf je het volledige bedrag verschuldigd en vervalt je recht op de gerechten.</li>
        <li>Wil je je bestelling wijzigen — meer porties, andere gerechten? Dat kan tot 72 uur vóór je ophaalmoment.</li>
        <li>Wordt je bestelling duurder door een wijziging? Dan betaal je het verschil vóór het ophaalmoment.</li>
        <li>Wordt je bestelling goedkoper? Dan krijg je het verschil niet terug. Het oorspronkelijke bedrag blijft verschuldigd.</li>
      </OL>
    </Article>

    <Article n={7} title="Allergenen en dieetwensen">
      <OL>
        <li>Wij koken in één keuken. Daar werken we met meerdere ingrediënten en allergenen. Kruisbesmetting kunnen we niet uitsluiten — wees je daarvan bewust.</li>
        <li>Op onze website vind je een actueel overzicht van ingrediënten en allergenen per gerecht. Check dat vóór je bestelt.</li>
        <li>Jij bent verantwoordelijk voor het informeren van je gasten over wat er in de gerechten zit.</li>
        <li>Wij maken geen aangepaste gerechten voor specifieke dieetwensen of allergieën. Wil je iets bijzonders? Overleg het dan vooraf met ons, en we kijken wat mogelijk is.</li>
      </OL>
    </Article>

    <Article n={8} title="Voedselveiligheid">
      <OL>
        <li>Wij werken volgens de HACCP-richtlijnen en de hygiënecode voor de horeca. Alles wat onze keuken verlaat, is met zorg bereid.</li>
        <li>Na het ophalen ben jij verantwoordelijk voor het op temperatuur houden en serveren.</li>
        <li>Gaat er iets mis door onjuist transport of opslag bij jou? Dan ligt dat niet bij ons.</li>
      </OL>
    </Article>

    <Article n={9} title="Aansprakelijkheid">
      <p>Waar we voor staan — en waar we niet voor kunnen instaan:</p>
      <OL>
        <li>Wij behandelen, bewaren, bereiden, verwerken en verpakken al onze gerechten met de grootste zorg en aandacht voor hygiëne. We volgen daarbij de HACCP-richtlijnen, -regels en -voorschriften, en de toepasselijke hygiënecode voor de horeca.</li>
        <li>Hoe jouw lichaam reageert op ons eten, kunnen wij niet controleren. Wij zijn niet aansprakelijk voor ziekte of andere fysieke klachten die ontstaan na het eten van onze gerechten — wat de oorzaak ook is.</li>
        <li>Het is jouw eigen verantwoordelijkheid om vóór je bestelling te controleren of onze gerechten geschikt zijn voor jou en je gasten. Ons ingrediënten- en allergenenoverzicht staat op de website — gebruik het.</li>
        <li>Zijn we aantoonbaar tekortgeschoten in wat we met jou hebben afgesproken? Dan zijn we uitsluitend aansprakelijk voor de directe schade die daaruit voortvloeit.</li>
        <li>Onze aansprakelijkheid is altijd beperkt tot het bedrag dat je voor die specifieke bestelling hebt betaald. Meer niet.</li>
        <li>
          Wij zijn niet aansprakelijk voor:
          <UL>
            <li>Schade door onjuist transport of opslag aan jouw kant</li>
            <li>Indirecte schade — zoals gevolgschade, gederfde winst of gemiste besparingen</li>
          </UL>
        </li>
      </OL>
    </Article>

    <Article n={10} title="Klachten">
      <OL>
        <li>Is er iets niet goed? Laat het ons binnen 24 uur na het ophalen weten, per e-mail.</li>
        <li>We doen ons best om binnen 5 werkdagen te reageren.</li>
        <li>Is je klacht terecht? Dan zoeken we samen naar een redelijke oplossing — bijvoorbeeld een gedeeltelijke terugbetaling of een tegoedbon voor een volgende bestelling.</li>
      </OL>
    </Article>

    <Article n={11} title="Privacy">
      <OL>
        <li>Je gegevens gebruiken we alleen om je bestelling uit te voeren: naam, e-mailadres en telefoonnummer.</li>
        <li>We delen je gegevens niet met anderen, tenzij dat nodig is voor de betaling (Mollie) of omdat de wet het verplicht.</li>
        <li>Je betalingsgegevens worden uitsluitend door Mollie verwerkt. Wij zien nooit je bank- of creditcardgegevens.</li>
      </OL>
    </Article>

    <Article n={12} title="Welke regels gelden">
      <OL>
        <li>Op al onze afspraken is het Nederlands recht van toepassing.</li>
        <li>Komen we er samen niet uit? Dan wordt het geschil voorgelegd aan de bevoegde rechter in het arrondissement waar wij gevestigd zijn.</li>
      </OL>
    </Article>

    <p className="mt-10 pt-6 border-t-2 border-[#E8F5E9] font-[700] text-[#0D3B4C]">
      Einde Algemene Voorwaarden. Versie 1.3 — 28 juli 2026.
    </p>
  </LegalPage>
);
const AllergStatus = ({ s }) => {
  const map = {
    ja: { cls: "bg-[#1B6B2E] text-white", Icon: Check, label: "Ja" },
    nee: { cls: "bg-[#F1F1F1] text-[#6b7280]", Icon: X, label: "Nee" },
    mogelijk: { cls: "bg-[#F4D03F] text-[#0D3B4C]", Icon: AlertTriangle, label: "Mogelijk" },
  };
  const { cls, Icon, label } = map[s] || map.nee;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-[700] whitespace-nowrap ${cls}`}>
      <Icon size={13} /> {label}
    </span>
  );
};

const DishAllergens = ({ name, desc, rows, testid }) => (
  <div className="mt-6 border-2 border-[#E8F5E9] bg-white" data-testid={testid}>
    <div className="bg-[#FEF9E7] px-4 sm:px-5 py-3 border-b-2 border-[#E8F5E9]">
      <h3 className="font-[700] text-lg text-[#0D3B4C]">{name}</h3>
    </div>
    <div className="px-4 sm:px-5 py-4">
      <p className="text-sm font-[400] text-[#0D3B4C] mb-4">{desc}</p>
      <ul className="divide-y divide-[#E8F5E9]">
        {rows.map((r) => (
          <li key={r.name} className="flex items-start justify-between gap-3 py-2.5">
            <div className="min-w-0">
              <span className={`font-[700] ${r.s === "nee" ? "text-[#9AA0A6]" : "text-[#0D3B4C]"}`}>{r.name}</span>
              {r.note && <p className="text-sm text-[#0D3B4C]/70 mt-0.5">{r.note}</p>}
            </div>
            <div className="flex-shrink-0 pt-0.5"><AllergStatus s={r.s} /></div>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

const ALLERGENS_14 = [
  ["1", "Glutenbevattende granen (tarwe, rogge, gerst, haver, spelt)", "G"],
  ["2", "Schaaldieren", "S"],
  ["3", "Eieren", "E"],
  ["4", "Vis", "V"],
  ["5", "Pinda's", "P"],
  ["6", "Soja", "So"],
  ["7", "Melk (inclusief lactose)", "M"],
  ["8", "Noten (amandel, hazelnoot, walnoot, cashew, etc.)", "N"],
  ["9", "Selderij", "Se"],
  ["10", "Mosterd", "Mo"],
  ["11", "Sesamzaad", "Ses"],
  ["12", "Sulfiet (≥10 mg/kg)", "Sul"],
  ["13", "Lupine", "L"],
  ["14", "Weekdieren", "W"],
];

const NASI = [
  { name: "Gluten", s: "ja", note: "Ketjap in BBQ-marinade bevat tarwe. Kippenbouillon (Knorr/Maggi) kan gluten bevatten." },
  { name: "Soja", s: "ja", note: "Ketjap is op basis van soja. Bouillon kan soja bevatten." },
  { name: "Eieren", s: "ja", note: "Ei-slertjes door de nasi." },
  { name: "Selderij", s: "ja", note: "Verse selderij in de nasi. Ook mogelijk in bouillon." },
  { name: "Mosterd", s: "nee" }, { name: "Sesam", s: "nee" }, { name: "Pinda's", s: "nee" }, { name: "Noten", s: "nee" },
  { name: "Melk", s: "nee", note: "Geen boter of margarine gebruikt." },
  { name: "Schaaldieren", s: "nee", note: "Geen trassi (garnalenpasta) in de nasi-kruiden." },
  { name: "Vis", s: "nee" }, { name: "Sulfiet", s: "nee" }, { name: "Lupine", s: "nee" }, { name: "Weekdieren", s: "nee" },
];
const BAMI = [
  { name: "Gluten", s: "ja", note: "Bami is op basis van tarwe. Ketjap in BBQ-marinade. Bouillon (Knorr/Maggi) kan gluten bevatten." },
  { name: "Soja", s: "ja", note: "Ketjap is op basis van soja. Bouillon kan soja bevatten." },
  { name: "Eieren", s: "ja", note: "Bami deels van ei gemaakt. Ei-slertjes bovenop." },
  { name: "Selderij", s: "ja", note: "Verse selderij in de bami. Ook mogelijk in bouillon." },
  { name: "Mosterd", s: "nee" }, { name: "Sesam", s: "nee" }, { name: "Pinda's", s: "nee" }, { name: "Noten", s: "nee" },
  { name: "Melk", s: "nee", note: "Geen boter of margarine gebruikt." },
  { name: "Schaaldieren", s: "nee", note: "Geen trassi in de bami-kruiden." },
  { name: "Vis", s: "nee" }, { name: "Sulfiet", s: "nee" }, { name: "Lupine", s: "nee" }, { name: "Weekdieren", s: "nee" },
];
const CHINESE = [
  { name: "Gluten", s: "ja", note: "Lichte sojasaus en oestersaus bevatten tarwe. Paneermeel (frituurlaag) kan gluten bevatten." },
  { name: "Soja", s: "ja", note: "Lichte sojasaus en oestersaus — beide op basis van soja." },
  { name: "Eieren", s: "nee" },
  { name: "Selderij", s: "mogelijk", note: "Kan in kruidenmixen of bouillon zitten." },
  { name: "Mosterd", s: "nee" },
  { name: "Sesam", s: "nee", note: "Geen sesamolie gebruikt — alleen frituurolie en zonnebloemolie." },
  { name: "Pinda's", s: "nee" }, { name: "Noten", s: "nee" }, { name: "Melk", s: "nee" }, { name: "Schaaldieren", s: "nee" }, { name: "Vis", s: "nee" }, { name: "Sulfiet", s: "nee" }, { name: "Lupine", s: "nee" },
  { name: "Weekdieren", s: "ja", note: "Oestersaus bevat extract van oesters (weekdieren)." },
];

const SUMMARY = [
  { g: "Nasi BBQ Kip", cols: ["ja", "ja", "ja", "ja", "nee", "nee"], bijz: "—" },
  { g: "Bami BBQ Kip", cols: ["ja", "ja", "ja", "ja", "nee", "nee"], bijz: "Bami = tarwe + ei" },
  { g: "Chinese Kip", cols: ["ja", "ja", "nee", "mogelijk", "nee", "nee"], bijz: "Oestersaus (weekdieren: Ja) · Paksoi in oestersaus + lichte sojasaus" },
];

export const Allergenen = () => (
  <LegalPage title="Allergenenoverzicht">
    <div className="bg-[#FEF9E7] border-2 border-[#E8F5E9] p-5 -mt-2 mb-6" data-testid="al-meta">
      <p className="font-[700] text-[#0D3B4C] text-lg">De Zeeuwse Tori — Allergenenoverzicht</p>
      <dl className="mt-3 text-sm grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-x-3 gap-y-1">
        <dt className="font-[700]">Versie:</dt><dd>1.1</dd>
        <dt className="font-[700]">Datum:</dt><dd>28 juli 2026</dd>
      </dl>
    </div>

    <p>Wij maken ons eten met zorg en met verse ingrediënten. Toch is het goed om te weten: wij koken in één keuken. Alle onderstaande allergenen komen daarin voor. Kruisbesmetting kunnen we niet uitsluiten.</p>
    <p>Check hieronder per gerecht welke allergenen erin zitten. Twijfel je ergens over? Neem dan vóór je bestelling contact met ons op. Beter één vraag te veel dan één hap te veel.</p>

    <h2 className="text-xl md:text-2xl font-[700] text-[#0D3B4C] tracking-tight mt-8 mb-3">De 14 wettelijke allergenen (EU 1169/2011)</h2>
    <div className="overflow-x-auto not-prose border-2 border-[#E8F5E9]" data-testid="al-14-table">
      <table className="w-full text-left text-sm border-collapse">
        <thead>
          <tr className="bg-[#0D3B4C] text-white">
            <th className="px-3 py-2 font-[700] w-12">Nr</th>
            <th className="px-3 py-2 font-[700]">Allergeen</th>
            <th className="px-3 py-2 font-[700] w-16">Code</th>
          </tr>
        </thead>
        <tbody>
          {ALLERGENS_14.map(([nr, naam, code], i) => (
            <tr key={nr} className={i % 2 ? "bg-[#FEF9E7]" : "bg-white"}>
              <td className="px-3 py-2 font-[700] text-[#1B6B2E]">{nr}</td>
              <td className="px-3 py-2 text-[#0D3B4C]">{naam}</td>
              <td className="px-3 py-2 font-[700] text-[#0D3B4C]">{code}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <h2 className="text-xl md:text-2xl font-[700] text-[#0D3B4C] tracking-tight mt-10 mb-1">Allergenen per gerecht</h2>
    <DishAllergens testid="al-dish-nasi" name="Nasi BBQ Kip"
      desc="Onze nasi: geurige Surinaamse nasi met geplozen kip, ei-sliertjes, verse selderij, bakbanaan, rauwkost van tomaat en komkommer, en een malse BBQ kippenpoot met ketjapsaus en sambal."
      rows={NASI} />
    <DishAllergens testid="al-dish-bami" name="Bami BBQ Kip"
      desc="Onze bami: Surinaamse bami (deels op basis van ei), met ei-sliertjes, verse selderij, rauwkost van tomaat en komkommer, en een malse BBQ kippenpoot met ketjapsaus en sambal."
      rows={BAMI} />
    <DishAllergens testid="al-dish-chinese" name="Chinese Kip"
      desc="Onze Chinese kip: luchtige basmatirijst met knapperig gefrituurde Chinese kip (met bot), een bedje van knapperige paksoi gewokt in oestersaus en lichte sojasaus, en pepersaus met sambal. Gebakken in frituurolie of zonnebloemolie — géén sesamolie."
      rows={CHINESE} />

    <h2 className="text-xl md:text-2xl font-[700] text-[#0D3B4C] tracking-tight mt-10 mb-3">Samenvatting</h2>
    <div className="overflow-x-auto not-prose border-2 border-[#E8F5E9]" data-testid="al-summary-table">
      <table className="w-full text-left text-sm border-collapse min-w-[720px]">
        <thead>
          <tr className="bg-[#0D3B4C] text-white">
            {["Gerecht", "Gluten", "Soja", "Eieren", "Selderij", "Mosterd", "Sesam", "Bijzonderheden"].map((h) => (
              <th key={h} className="px-3 py-2 font-[700] align-bottom">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {SUMMARY.map((row, i) => (
            <tr key={row.g} className={i % 2 ? "bg-[#FEF9E7]" : "bg-white"}>
              <td className="px-3 py-2 font-[700] text-[#0D3B4C] whitespace-nowrap">{row.g}</td>
              {row.cols.map((c, j) => (
                <td key={j} className="px-3 py-2"><AllergStatus s={c} /></td>
              ))}
              <td className="px-3 py-2 text-[#0D3B4C] min-w-[200px]">{row.bijz}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <div className="mt-10 border-l-4 border-[#B71C1C] bg-[#FEECEC] p-5" data-testid="al-disclaimer">
      <div className="flex items-center gap-2 mb-2">
        <Info size={18} className="text-[#B71C1C]" />
        <h3 className="font-[700] text-[#0D3B4C]">Allergeneninformatie</h3>
      </div>
      <div className="space-y-2 text-[#0D3B4C]">
        <p>Bij De Zeeuwse Tori koken we met verse ingrediënten in één keuken waar alle bovenstaande allergenen worden verwerkt. We doen ons uiterste best om kruisbesmetting te voorkomen, maar kunnen het niet volledig uitsluiten.</p>
        <p>Dit overzicht is met zorg samengesteld op basis van onze ingrediënten en bereidingswijze. Toch kunnen recepten wijzigen of leveranciers hun samenstelling aanpassen. Twijfel je over een allergeen? Stuur ons dan vóór je bestelling een bericht. We helpen je graag verder.</p>
        <p className="font-[700]">Ben je allergisch voor één van de vermelde allergenen? Dan raden we je aan om geen gerechten van De Zeeuwse Tori te eten. Jouw gezondheid gaat voor — altijd.</p>
      </div>
    </div>

    <p className="mt-10 pt-6 border-t-2 border-[#E8F5E9] font-[700] text-[#0D3B4C]">
      Einde Allergenenoverzicht. Versie 1.1 — 28 juli 2026.
    </p>
  </LegalPage>
);
const PrivSection = ({ n, title, children }) => (
  <section className="mt-8" data-testid={`pv-sectie-${n}`}>
    <h2 className="text-xl md:text-2xl font-[700] text-[#0D3B4C] tracking-tight mb-3">
      {n}. {title}
    </h2>
    <div className="space-y-2 font-[400] text-[#0D3B4C] leading-relaxed">{children}</div>
  </section>
);

export const Privacy = () => (
  <LegalPage title="Privacyverklaring">
    <div className="bg-[#FEF9E7] border-2 border-[#E8F5E9] p-5 -mt-2 mb-6" data-testid="pv-meta">
      <p className="font-[700] text-[#0D3B4C] text-lg">De Zeeuwse Tori — Privacyverklaring</p>
      <dl className="mt-3 text-sm grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-x-3 gap-y-1">
        <dt className="font-[700]">Versie:</dt><dd>1.0</dd>
        <dt className="font-[700]">Datum:</dt><dd>28 juli 2026</dd>
      </dl>
    </div>

    <p>
      Wij vinden je privacy belangrijk. Daarom zijn we open over welke gegevens we van je verzamelen,
      waarom we dat doen en wat je rechten zijn. Geen kleine lettertjes, geen verborgen clausules. Gewoon duidelijk.
    </p>

    <PrivSection n={1} title="Wie is verantwoordelijk voor je gegevens?">
      <p>De Zeeuwse Tori is de verwerkingsverantwoordelijke voor je persoonsgegevens. Dat betekent: wij bepalen wat er met je gegevens gebeurt en wij zorgen dat dat veilig en volgens de wet gaat.</p>
      <div className="bg-white border-2 border-[#E8F5E9] p-4 not-prose">
        <p className="font-[700]">De Zeeuwse Tori</p>
        <p>Duke Ellingtonstraat 109</p>
        <p>KVK: 42115378</p>
        <p>BTW: NL005505789B34</p>
        <p>E-mail: <a href="mailto:info@zeeuwsetori.nl" className="text-[#1B6B2E] font-[600] underline">info@zeeuwsetori.nl</a></p>
      </div>
    </PrivSection>

    <PrivSection n={2} title="Welke gegevens verzamelen we?">
      <p>Alleen wat nodig is om je bestelling uit te voeren. Dat is:</p>
      <UL>
        <li><span className="font-[700]">Je naam</span> — zodat we weten wie er aan de deur staat</li>
        <li><span className="font-[700]">Je e-mailadres</span> — voor je orderbevestiging</li>
        <li><span className="font-[700]">Je telefoonnummer</span> — voor het geval we je snel moeten bereiken over je bestelling</li>
        <li><span className="font-[700]">Je bestelling</span> — welke gerechten, hoeveel porties, voor welke dag en welk tijdvenster</li>
      </UL>
      <p>Dat is het. We verzamelen niets anders. Geen profielen, geen interesses, geen surfgedrag.</p>
    </PrivSection>

    <PrivSection n={3} title="Waarom verzamelen we deze gegevens?">
      <p>We gebruiken je gegevens uitsluitend om:</p>
      <UL>
        <li>Je bestelling te verwerken en uit te voeren</li>
        <li>Je een orderbevestiging te sturen</li>
        <li>Contact met je op te nemen als er iets met je bestelling is</li>
      </UL>
      <p>De wettelijke grondslag is: uitvoering van de overeenkomst (AVG artikel 6, lid 1b). Zonder deze gegevens kunnen we simpelweg niet voor je koken.</p>
    </PrivSection>

    <PrivSection n={4} title="Wie krijgt je gegevens te zien?">
      <p>Bijna niemand.</p>
      <UL>
        <li><span className="font-[700]">Wijzelf</span> — om je bestelling klaar te maken</li>
        <li><span className="font-[700]">Mollie</span> — onze betaalprovider. Mollie verwerkt je iDEAL/WERO-betaling en ziet daarbij je bankgegevens. Wij zien die nooit.</li>
        <li><span className="font-[700]">Verder niemand.</span> We verkopen je gegevens niet, we delen ze niet met adverteerders, en we sturen ze niet naar landen buiten de EU.</li>
      </UL>
    </PrivSection>

    <PrivSection n={5} title="Hoe lang bewaren we je gegevens?">
      <p>Niet langer dan nodig.</p>
      <UL>
        <li><span className="font-[700]">Bestelgegevens</span> bewaren we 7 jaar — dat moet van de Belastingdienst (fiscale bewaarplicht).</li>
        <li><span className="font-[700]">Je naam, e-mailadres en telefoonnummer</span> bewaren we tot 1 jaar na je laatste bestelling. Daarna verwijderen we ze.</li>
        <li>Wil je dat we je gegevens eerder verwijderen? Dat kan. Zie artikel 7.</li>
      </UL>
    </PrivSection>

    <PrivSection n={6} title="Hoe beveiligen we je gegevens?">
      <p>We nemen passende technische en organisatorische maatregelen om je gegevens te beschermen tegen verlies, diefstal of onbevoegde toegang. Onze website draait via een beveiligde verbinding (HTTPS) en je betaling loopt uitsluitend via Mollie — een gecertificeerde betaalprovider die voldoet aan alle Europese veiligheidsnormen.</p>
    </PrivSection>

    <PrivSection n={7} title="Wat zijn je rechten?">
      <p>Je hebt de volgende rechten onder de AVG:</p>
      <UL>
        <li><span className="font-[700]">Inzage:</span> je mag ons vragen welke gegevens we van je hebben.</li>
        <li><span className="font-[700]">Correctie:</span> klopt er iets niet? Dan passen we het aan.</li>
        <li><span className="font-[700]">Verwijdering:</span> wil je dat we je gegevens wissen? Dat doen we — tenzij we ze wettelijk moeten bewaren (zie artikel 5).</li>
        <li><span className="font-[700]">Beperking:</span> je kunt ons vragen het gebruik van je gegevens tijdelijk te beperken.</li>
        <li><span className="font-[700]">Overdraagbaarheid:</span> je kunt je gegevens in een leesbaar formaat opvragen.</li>
        <li><span className="font-[700]">Bezwaar:</span> je kunt bezwaar maken tegen de verwerking van je gegevens.</li>
      </UL>
      <p>Stuur je verzoek per e-mail naar <a href="mailto:info@zeeuwsetori.nl" className="text-[#1B6B2E] font-[600] underline">info@zeeuwsetori.nl</a>. We reageren binnen 30 dagen.</p>
      <p>Ben je niet tevreden over hoe we met je gegevens omgaan? Dan kun je een klacht indienen bij de Autoriteit Persoonsgegevens (<a href="https://autoriteitpersoonsgegevens.nl" target="_blank" rel="noreferrer" className="text-[#1B6B2E] font-[600] underline">autoriteitpersoonsgegevens.nl</a>).</p>
    </PrivSection>

    <PrivSection n={8} title="Cookies">
      <p>Onze website gebruikt alleen functionele cookies — die zijn nodig om de website te laten werken. Denk aan: onthouden wat er in je bestelling zit terwijl je het menu samenstelt.</p>
      <p>We gebruiken geen trackingcookies, geen analytische cookies en geen cookies van derden. Wat er op je bord komt, is onze zaak. Wat je verder op internet doet, is de jouwe.</p>
    </PrivSection>

    <PrivSection n={9} title="Wijzigingen">
      <p>We kunnen deze privacyverklaring aanpassen als onze werkwijze of de wet verandert. De meest actuele versie staat altijd op onze website.</p>
    </PrivSection>

    <PrivSection n={10} title="Contact">
      <p>Vragen over je privacy? Mail ons op <a href="mailto:info@zeeuwsetori.nl" className="text-[#1B6B2E] font-[600] underline">info@zeeuwsetori.nl</a>. We denken graag met je mee.</p>
    </PrivSection>

    <p className="mt-10 pt-6 border-t-2 border-[#E8F5E9] font-[700] text-[#0D3B4C]">
      Einde Privacyverklaring. Versie 1.0 — 28 juli 2026.
    </p>
  </LegalPage>
);
